/**
 * Toast Context Provider
 * 
 * Manages toast notification system for displaying temporary messages throughout
 * the application. Supports multiple toast types (success, error, info, warning)
 * and handles toast lifecycle management with automatic removal.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import Toast from '@/shared/components/feedback/Toast.jsx';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [counter, setCounter] = useState(0);
  const [lastMessages, setLastMessages] = useState(new Map());

  const showToast = useCallback((message, type = 'success', duration = 3000) => {
    if (!message) return;

    // Throttling: Prevent duplicate messages within 5 seconds
    const now = Date.now();
    const lastTime = lastMessages.get(message);
    if (lastTime && now - lastTime < 5000) {
      return;
    }

    setLastMessages(prev => new Map(prev).set(message, now));

    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${counter}`;
    setToasts((prev) => [...prev, { id, message, type, duration }]);
    setCounter((prev) => prev + 1);
  }, [counter, lastMessages]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const success = useCallback((message, duration) => {
    showToast(message, 'success', duration);
  }, [showToast]);

  const error = useCallback((message, duration) => {
    showToast(message, 'error', duration);
  }, [showToast]);

  const info = useCallback((message, duration) => {
    showToast(message, 'info', duration);
  }, [showToast]);

  const warning = useCallback((message, duration) => {
    showToast(message, 'warning', duration);
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, success, error, info, warning }}>
      {children}
      {/* ARIA live region for toast notifications - screen readers will announce new toasts */}
      <div
        className="toast-wrapper"
        role="region"
        aria-live="polite"
        aria-atomic="false"
        aria-label="Notifications"
      >
        {toasts.map((toast, index) => (
          <div
            key={toast.id}
            className="toast-item"
            style={{
              marginTop: index > 0 ? '12px' : '0'
            }}
          >
            <Toast
              message={toast.message}
              type={toast.type}
              duration={toast.duration}
              onClose={() => removeToast(toast.id)}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
